import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Images, ClipboardList, ShieldCheck, Eye, IndianRupee, CalendarClock, HardHat } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Label } from "@/components/site/Motion";
import { PageHero, CTASection } from "@/components/site/Sections";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const FILTERS = ["All", "Construction", "Interior", "Furniture"];
const STATUS = ["All", "Completed", "Ongoing"];

const HIGHLIGHTS = [
  { icon: ClipboardList, label: "Project Planning" },
  { icon: ShieldCheck, label: "Quality Control" },
  { icon: Eye, label: "Site Monitoring" },
  { icon: IndianRupee, label: "Budget Management" },
  { icon: CalendarClock, label: "Timeline Management" },
  { icon: HardHat, label: "Expert Supervision" },
];

function galleryOf(p) {
  const imgs = Array.isArray(p.images) && p.images.length ? p.images : [];
  const all = [p.image, ...imgs].filter(Boolean);
  return [...new Set(all)];
}

export default function Portfolio() {
  const [filter, setFilter] = useState("All");
  const [status, setStatus] = useState("All");
  const [active, setActive] = useState(null); // project
  const [slide, setSlide] = useState(0);

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["projects", "all"],
    queryFn: async () => (await api.get("/projects")).data,
  });

  const filtered = projects.filter(
    (p) => (filter === "All" || p.category === filter) &&
           (status === "All" || (p.status || "completed").toLowerCase() === status.toLowerCase())
  );
  const gallery = active ? galleryOf(active) : [];

  const open = (p) => { setActive(p); setSlide(0); };
  const next = () => setSlide((s) => (s + 1) % gallery.length);
  const prev = () => setSlide((s) => (s - 1 + gallery.length) % gallery.length);

  return (
    <div data-testid="portfolio-page">
      <PageHero
        eyebrow="Portfolio"
        title="Work that speaks in concrete, light and detail."
        subtitle="A selection of construction, interior and furniture projects delivered across Lucknow and Uttar Pradesh."
        image="https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&q=85"
      />

      <section className="bg-navy text-white grain relative overflow-hidden">
        <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-8 py-14">
          <Label>How we deliver every project</Label>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {HIGHLIGHTS.map((h, i) => {
              const Icon = h.icon;
              return (
                <Reveal key={h.label} delay={i * 0.05}>
                  <div className="flex flex-col items-center text-center gap-3 rounded-sm border border-white/10 p-5 h-full">
                    <Icon className="h-7 w-7 text-gold" strokeWidth={1.4} />
                    <span className="text-sm text-white/80 leading-snug">{h.label}</span>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-16 lg:py-24">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`portfolio-filter-${f.toLowerCase()}`}
              onClick={() => setFilter(f)}
              className={`rounded-sm px-6 py-2.5 text-sm tracking-wide border transition-colors duration-300 ${
                filter === f ? "bg-navy text-white border-navy" : "bg-transparent text-navy border-navy/20 hover:border-navy"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-12">
          <span className="text-xs uppercase tracking-[0.2em] text-navy/40 mr-1">Status</span>
          {STATUS.map((st) => (
            <button
              key={st}
              data-testid={`portfolio-status-${st.toLowerCase()}`}
              onClick={() => setStatus(st)}
              className={`rounded-full px-4 py-1.5 text-xs tracking-wide border transition-colors duration-300 ${
                status === st ? "bg-gold text-navy border-gold" : "bg-transparent text-navy/60 border-navy/20 hover:border-navy"
              }`}
            >
              {st}
            </button>
          ))}
        </div>

        {isLoading ? (
          <p className="text-navy/50">Loading projects…</p>
        ) : filtered.length === 0 ? (
          <p className="text-navy/50" data-testid="portfolio-empty">No projects in this category yet.</p>
        ) : (
          <motion.div layout className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {filtered.map((p, i) => {
                const count = galleryOf(p).length;
                const ongoing = (p.status || "completed").toLowerCase() === "ongoing";
                return (
                  <motion.button
                    key={p.id}
                    layout
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
                    onClick={() => open(p)}
                    data-testid="portfolio-card"
                    className="group relative text-left overflow-hidden rounded-sm bg-navy"
                  >
                    <div className="relative h-80 overflow-hidden">
                      <img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/90 via-transparent to-transparent" />
                      <span className={`absolute top-4 left-4 rounded-full px-3 py-1 text-xs ${ongoing ? "bg-gold text-navy" : "bg-white/90 text-navy"}`}>
                        {ongoing ? "Ongoing" : "Completed"}
                      </span>
                      {count > 1 && (
                        <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-navy-deep/70 backdrop-blur px-3 py-1 text-xs text-white">
                          <Images className="h-3.5 w-3.5" /> {count}
                        </span>
                      )}
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-6">
                      <span className="text-xs uppercase tracking-[0.2em] text-gold">
                        {p.category}{p.year ? ` · ${p.year}` : ""}
                      </span>
                      <h3 className="mt-2 font-display text-2xl text-white">{p.title}</h3>
                      {p.location && <p className="mt-1 text-sm text-white/60">{p.location}</p>}
                    </div>
                  </motion.button>
                );
              })}
            </AnimatePresence>
          </motion.div>
        )}
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-4xl rounded-sm p-0 overflow-hidden bg-navy-deep border-navy" data-testid="portfolio-gallery">
          {active && (
            <div>
              <DialogTitle className="sr-only">{active.title}</DialogTitle>
              <div className="relative bg-black">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={slide}
                    src={gallery[slide]}
                    alt={`${active.title} ${slide + 1}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="w-full h-[52vh] object-cover"
                  />
                </AnimatePresence>
                {gallery.length > 1 && (
                  <>
                    <button onClick={prev} data-testid="gallery-prev" className="absolute left-3 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-navy-deep/70 text-white hover:bg-gold hover:text-navy transition-colors">
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button onClick={next} data-testid="gallery-next" className="absolute right-3 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-navy-deep/70 text-white hover:bg-gold hover:text-navy transition-colors">
                      <ChevronRight className="h-5 w-5" />
                    </button>
                    <span className="absolute bottom-3 right-3 rounded-full bg-navy-deep/70 px-3 py-1 text-xs text-white">
                      {slide + 1} / {gallery.length}
                    </span>
                  </>
                )}
              </div>
              <div className="p-6 lg:p-8 text-white">
                <span className="text-xs uppercase tracking-[0.2em] text-gold">
                  {active.category}{active.location ? ` · ${active.location}` : ""}{active.year ? ` · ${active.year}` : ""}
                </span>
                <h3 className="mt-2 font-display text-3xl">{active.title}</h3>
                {active.description && <p className="mt-3 text-white/60 leading-relaxed max-w-2xl">{active.description}</p>}
                {gallery.length > 1 && (
                  <div className="mt-6 flex gap-3 overflow-x-auto pb-1">
                    {gallery.map((g, i) => (
                      <button
                        key={i}
                        onClick={() => setSlide(i)}
                        className={`relative h-16 w-24 shrink-0 overflow-hidden rounded-sm border-2 transition-colors ${i === slide ? "border-gold" : "border-transparent opacity-60 hover:opacity-100"}`}
                      >
                        <img src={g} alt="" className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CTASection />
    </div>
  );
}
